import { navigate } from "gatsby";
import { fitFace } from "./product-face";

/* The orbit-to-case-study transition, after offmenu.design's.

   The clicked product square is cloned into a fixed layer above the page
   while a white sheet fades in behind it. It first moves halfway to the
   screen center as a 50vw x 50vh rounded rectangle (0.5 s), then grows into
   the exact box of the case study's header photo (0.6 s). Then the route
   changes underneath. The layer lives on <body>, so it survives the route
   change; the case page calls signalCaseHeroReady() once its header is
   painted, and the layer fades away over an identical image.

   Going back reverses it. Before the home page renders, the header photo
   is cloned at its current box over an opaque sheet. Once the orbit has
   settled at the restored scroll position, it hands over its square for
   that product. The photo then shrinks into the square while the sheet
   fades out. If the square is not on screen (for example, the logo link
   opens the page at the top), the layer simply fades away. */

const READY_EVENT = "neu:case-hero-ready";
let active = null;

export function caseHeroBox() {
  return {
    top: 80,
    left: 16,
    width: window.innerWidth - 32,
    height: window.innerHeight - 96,
    radius: 24,
  };
}

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function tween(from, to, duration, onFrame) {
  return new Promise((resolve) => {
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const k = easeInOutQuad(t);
      const box = {};
      Object.keys(to).forEach((key) => {
        box[key] = from[key] + (to[key] - from[key]) * k;
      });
      onFrame(box);
      if (t < 1) requestAnimationFrame(step);
      else resolve();
    };
    requestAnimationFrame(step);
  });
}

function finish() {
  if (!active) return;
  const { layer, timer } = active;
  window.clearTimeout(timer);
  window.removeEventListener(READY_EVENT, finish);
  active = null;
  // Two frames, so the case page's own header has painted underneath.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      layer.style.transition = "opacity 250ms ease-out";
      layer.style.opacity = "0";
      window.setTimeout(() => layer.remove(), 300);
    })
  );
}

/* A fixed layer with a white sheet and a frame holding a copy of a face.
   apply(box) moves the frame and refits the face. */
function buildLayer(face, color) {
  const layer = document.createElement("div");
  layer.className = "case-transition";
  const sheet = document.createElement("div");
  sheet.className = "case-transition-sheet";
  const frame = document.createElement("div");
  frame.className = "face-frame case-transition-frame";
  frame.style.setProperty("--card-color", color);
  const clone = face.cloneNode(true);
  clone.style.transform = "";
  frame.appendChild(clone);
  layer.append(sheet, frame);
  document.body.appendChild(layer);
  clone.querySelectorAll("video").forEach((video) => {
    video.muted = true;
    video.play().catch(() => {});
  });

  const apply = (box) => {
    frame.style.top = `${box.top}px`;
    frame.style.left = `${box.left}px`;
    frame.style.width = `${box.width}px`;
    frame.style.height = `${box.height}px`;
    frame.style.borderRadius = `${box.radius}px`;
    fitFace(clone, box.width, box.height);
  };
  return { layer, sheet, frame, apply };
}

function fadeOut(layer, duration = 350) {
  layer.style.transition = `opacity ${duration}ms ease-out`;
  layer.style.opacity = "0";
  window.setTimeout(() => layer.remove(), duration + 50);
}

let returning = null;
let caseScrollY = 0;

/* The case page reports its scroll position every frame. On Back, the
   browser restores the home page's scroll position before the route-change
   hook runs, while the case study is still on screen, so the header's live
   position is wrong by then; the last frame's position is not. */
export function noteCaseScroll(y) {
  caseScrollY = y;
}

/* Called before a route change. Prepares the return layer when leaving a
   case study for the home page. */
export function prepareReturnTransition(location, prevLocation) {
  if (returning || active) return;
  if (!prevLocation || location?.pathname !== "/") return;
  const match = prevLocation.pathname.match(/^\/products\/([^/]+)\/?$/);
  if (!match) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const hero = document.querySelector(".case-hero-media");
  const face = hero?.querySelector(".orb-face");
  if (!hero || !face) return;

  const live = hero.getBoundingClientRect();
  const top = live.top + window.scrollY - caseScrollY;
  const rect = { top, left: live.left, width: live.width, height: live.height, bottom: top + live.height };
  const color = getComputedStyle(hero).getPropertyValue("--card-color");
  const { layer, sheet, frame, apply } = buildLayer(face, color);
  sheet.style.transition = "none";
  sheet.style.opacity = "1";
  const visible = rect.bottom > 120;
  const start = visible
    ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height, radius: 24 }
    : caseHeroBox();
  apply(start);
  // When the header had been scrolled away, there is nothing to carry back;
  // only the sheet is shown, and it fades.
  if (!visible) frame.style.opacity = "0";

  returning = {
    slug: match[1],
    layer,
    sheet,
    apply,
    start,
    visible,
    timer: window.setTimeout(() => finishReturn(null), 2500),
  };
}

function finishReturn(getTarget) {
  if (!returning) return;
  const { slug, layer, sheet, apply, start, visible, timer } = returning;
  returning = null;
  window.clearTimeout(timer);

  const target = visible && getTarget ? getTarget(slug) : null;
  if (!target) {
    fadeOut(layer);
    return;
  }

  target.element.style.visibility = "hidden";
  sheet.style.transition = "opacity 500ms ease-out";
  requestAnimationFrame(() => {
    sheet.style.opacity = "0";
  });
  const end = {
    top: target.rect.top,
    left: target.rect.left,
    width: target.rect.width,
    height: target.rect.height,
    radius: target.radius,
  };
  tween(start, end, 650, apply).then(() => {
    target.element.style.visibility = "";
    fadeOut(layer, 150);
  });
}

/* Called by the home page's orbit once it has settled. getTarget(slug)
   returns { element, rect, radius } for the product's square, or null. */
export function completeReturnTransition(getTarget) {
  finishReturn(getTarget);
}

export function signalCaseHeroReady() {
  window.dispatchEvent(new Event(READY_EVENT));
}

/* source: the element to lift out (an orbit square, or a card image).
   round: true for an orbit item, which takes the two-step path; its corner
   starts at radiusRatio of its width (0.5 would be a circle). A card takes
   the one-step path from its own corner radius, startRadius px. */
export function startCaseTransition({ slug, source, color, round = true, radiusRatio = 0.5, startRadius = 16 }) {
  const href = `/products/${slug}/`;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const face = source?.querySelector(".orb-face");
  if (active || reduced || !face) {
    navigate(href);
    return;
  }

  const rect = source.getBoundingClientRect();
  const { layer, sheet, apply } = buildLayer(face, color);

  const start = {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    radius: round ? Math.max(rect.width, rect.height) * radiusRatio : startRadius,
  };
  apply(start);
  source.style.visibility = "hidden";

  requestAnimationFrame(() => {
    sheet.style.opacity = "1";
  });

  active = {
    layer,
    timer: window.setTimeout(finish, 2500),
  };
  window.addEventListener(READY_EVENT, finish);

  const target = caseHeroBox();
  const go = () => {
    // The page is not scrolled to the top here: Gatsby records the home
    // page's scroll position on leaving, and Back restores it, so the
    // orbit reopens on this product. Gatsby moves the new page to its top
    // behind the layer, with smooth scrolling off (gatsby-browser.js).
    navigate(href);
    window.setTimeout(() => {
      source.style.visibility = "";
    }, 400);
  };

  if (round) {
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const width = window.innerWidth * 0.5;
    const height = window.innerHeight * 0.5;
    const middle = {
      top: cy + (window.innerHeight / 2 - cy) * 0.5 - height / 2,
      left: cx + (window.innerWidth / 2 - cx) * 0.5 - width / 2,
      width,
      height,
      radius: 40,
    };
    tween(start, middle, 500, apply)
      .then(() => tween(middle, target, 600, apply))
      .then(go);
  } else {
    tween(start, target, 700, apply).then(go);
  }
}
